import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

// -------------------- Interfaces --------------------

export interface TenantDomain {
  admin_url: string;
  api_url: string;
  public_url?: string;
  web_ui_domain?: string;
  admin_ui_domain?: string;
  web_api_domain?: string;
  mobile_api_domain?: string;
  admin_api_domain?: string;
  ui_domains?: string;
  api_domains?: string;
}

export interface TenantEmailConfig {
  provider: string;
  host_name: string;
  host_port: number;
  client_id: string;
  client_secret: string;
  from: string;
}

export interface TenantLimits {
  max_users: number;
  max_document_stores: number;
  max_files_per_document_store: number;
}

export interface TenantOneSignalConfig {
  app_id: string;
  rest_api_key: string;
  enabled: boolean;
}

export interface TenantIngressHistory {
  commit_id: string;
  status: string;
  timestamp: string;
  action: string;
}

export interface TenantDnsRecord {
  record_id: string;
  hostname: string;
  type: string;
  target: string;
  proxied: boolean;
  created_at: string;
}

export interface Tenant {
  _id: string;
  id: string;
  name: string;
  tenant_key: string;
  logo?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  allowed_domains: string[];
  domains: TenantDomain[];
  email_config: TenantEmailConfig;
  limits?: TenantLimits;
  onesignal_config?: TenantOneSignalConfig;
  suspension_config?: any;
  auth_config?: any;
  business_types?: string[];
  integrations?: {
    gitlab_ingress?: {
      commit_id: string;
      status: string;
      updated_at: string;
      history: TenantIngressHistory[];
    };
    cloudflare_dns?: {
      status: string;
      records: TenantDnsRecord[];
      history: any[];
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface TenantMetrics {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
}

export interface TenantListParams {
  skip?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface TenantListResponse {
  data: {
    total_count: number;
    last_modified: string;
    tenants: Tenant[];
  };
}

export interface TenantMetricsResponse {
  data: TenantMetrics;
}

export interface TenantAvailabilityResponse {
  data: {
    available: boolean;
    message: string;
  };
}

export interface TenantResponse {
  data: Tenant;
}

export interface TenantCreatePayload {
  name: string;
  tenant_key: string;
  status?: string;
  allowed_domains?: string[];
  domains: TenantDomain[];
  email_config?: Partial<TenantEmailConfig>;
  limits?: Partial<TenantLimits>;
  onesignal_config?: Partial<TenantOneSignalConfig>;
  logo?: string;
  auth_config?: any;
  business_types?: string[];
}

export type TenantUpdatePayload = Partial<Omit<TenantCreatePayload, 'tenant_key'>>;

export interface IngressYAMLResponse {
  data: { yaml: string; message: string };
}

export interface UpdateIngressYAMLPayload {
  yaml: string;
  commit_message: string;
}

export interface UpdateIngressYAMLResponse {
  data: { commit_id: string; message: string };
}

export interface IngressHistoryResponse {
  data: { total: number; message: string; history: TenantIngressHistory[] };
}

export interface CloudFlareDnsRecord {
  id: string;
  name: string;
  type: string;
  content: string;
  proxiable?: boolean;
  proxied: boolean;
  ttl?: number;
  created_on: string;
  modified_on?: string;
}

export interface TenantDnsRawRecord {
  record_id: string;
  hostname: string;
  type: string;
  target: string;
  proxied: boolean;
  created_at: string;
}

export interface DNSRecordsResponse {
  data: { total: number; message: string; records: CloudFlareDnsRecord[] };
}

export interface TenantDNSRecordsRawResponse {
  data: { total: number; message: string; records: TenantDnsRawRecord[] };
}

export interface DNSRecordPayload {
  type: string;
  name: string;
  content: string;
  ttl?: number;
  proxied?: boolean;
}

export interface DNSRecordResponse {
  data: { message: string; record: CloudFlareDnsRecord };
}

export interface DNSDeleteResponse {
  data: { success: boolean; message: string };
}

// -------------------- Service --------------------

class TenantService {
  async listTenants(params: TenantListParams): Promise<TenantListResponse> {
    const response = await apiClient.post(API_ENDPOINTS.TENANTS.LIST, params);
    return response.data;
  }

  async getMetrics(): Promise<TenantMetricsResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.METRICS);
    return response.data;
  }

  async checkKeyAvailability(key: string): Promise<TenantAvailabilityResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.CHECK_KEY(key));
    return response.data;
  }

  async checkNameAvailability(name: string): Promise<TenantAvailabilityResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.CHECK_NAME(name));
    return response.data;
  }

  async createTenant(payload: TenantCreatePayload): Promise<TenantResponse> {
    const response = await apiClient.post(API_ENDPOINTS.TENANTS.CREATE, payload);
    return response.data;
  }

  async getTenantByKey(key: string): Promise<TenantResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.GET_BY_KEY(key));
    return response.data;
  }

  async updateTenant(key: string, payload: TenantUpdatePayload): Promise<TenantResponse> {
    const response = await apiClient.put(API_ENDPOINTS.TENANTS.UPDATE(key), payload);
    return response.data;
  }

  async updateTenantStatus(key: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'): Promise<TenantResponse> {
    const response = await apiClient.put(API_ENDPOINTS.TENANTS.UPDATE_STATUS(key), { status });
    return response.data;
  }

  async deleteTenant(key: string): Promise<{ data: { success: boolean; message: string } }> {
    const response = await apiClient.delete(API_ENDPOINTS.TENANTS.DELETE(key));
    return response.data;
  }

  async getIngressYAML(): Promise<IngressYAMLResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.INGRESS_YAML);
    return response.data;
  }

  async updateIngressYAML(payload: UpdateIngressYAMLPayload): Promise<UpdateIngressYAMLResponse> {
    const response = await apiClient.put(API_ENDPOINTS.TENANTS.INGRESS_YAML, payload);
    return response.data;
  }

  async getIngressHistory(key: string): Promise<IngressHistoryResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.INGRESS_HISTORY(key));
    return response.data;
  }

  async getAllDNSRecords(): Promise<DNSRecordsResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.DNS_ALL);
    return response.data;
  }

  async getTenantDNSRecords(key: string): Promise<TenantDNSRecordsRawResponse> {
    const response = await apiClient.get(API_ENDPOINTS.TENANTS.DNS_BY_TENANT(key));
    return response.data;
  }

  async createDNSRecord(payload: DNSRecordPayload): Promise<DNSRecordResponse> {
    const response = await apiClient.post(API_ENDPOINTS.TENANTS.DNS_CREATE, payload);
    return response.data;
  }

  async updateDNSRecord(recordId: string, payload: DNSRecordPayload): Promise<DNSRecordResponse> {
    const response = await apiClient.put(API_ENDPOINTS.TENANTS.DNS_UPDATE(recordId), payload);
    return response.data;
  }

  async deleteDNSRecord(recordId: string): Promise<DNSDeleteResponse> {
    const response = await apiClient.delete(API_ENDPOINTS.TENANTS.DNS_DELETE(recordId));
    return response.data;
  }
}

export const tenantService = new TenantService();
export default tenantService;
